import { useState } from 'react';
import { DISPLAYED_REQUEST_STATUSES, type DisplayedRequestStatus } from '@product-portal/shared';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination } from '@/components/portal/pagination';
import { RequestsTable } from '@/components/portal/requests-table';
import { SelectField } from '@/components/portal/select-field';
import { useRequestList } from '@/hooks/use-requests';
import { STATUS_LABELS } from '@/lib/labels';

const PAGE_SIZE = 6;

/**
 * Card « Mes demandes » (screen 01): status filter, table, pagination.
 * The filter offers the six statuses only — « En attente de réponse » is an
 * indicator (highlighted row), never a status.
 */
export function RequestListCard({ title, scope }: { title: string; scope: 'mine' | 'team' }) {
  const [status, setStatus] = useState<DisplayedRequestStatus | ''>('');
  const [page, setPage] = useState(1);
  const list = useRequestList({ scope, status: status || undefined, page, pageSize: PAGE_SIZE });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <SelectField
          label="Statut"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as DisplayedRequestStatus | '');
            setPage(1);
          }}
        >
          <option value="">Tous les statuts</option>
          {DISPLAYED_REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </SelectField>
      </CardHeader>
      {list.isPending ? (
        <p className="m-0 px-5 py-4 text-sm text-muted-foreground">Chargement…</p>
      ) : list.isError ? (
        <p className="m-0 px-5 py-4 text-sm text-muted-foreground">Les demandes n'ont pas pu être chargées.</p>
      ) : list.data.total === 0 ? (
        <p className="m-0 px-5 py-4 text-sm text-muted-foreground">Aucune demande{status ? ' avec ce statut' : ''}.</p>
      ) : (
        <>
          <RequestsTable items={list.data.items} showRequester={scope === 'team'} />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data.total}
            itemLabel="demandes"
            onPageChange={setPage}
            ariaLabel={`Pagination de ${title.toLowerCase()}`}
          />
        </>
      )}
    </Card>
  );
}
